import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { PushToggle } from "@/components/pwa/PushToggle";
import {
  ArrowLeft,
  CalendarDays,
  ClipboardCheck,
  LogOut,
  MessageSquare,
  ShieldCheck,
  Clock,
  MapPin,
} from "lucide-react";

type Booking = {
  id: string;
  booking_date: string;
  booking_time: string;
  status: string;
  location_type: string;
  address: string | null;
  total_price: number;
  notes: string | null;
};

type ChangeRequest = {
  id: string;
  booking_id: string | null;
  request_type: string;
  message: string;
  status: string;
  admin_response: string | null;
  created_at: string;
};

const statusLabel: Record<string, string> = {
  pending: "Pendente",
  confirmed: "Confirmada",
  completed: "Concluída",
  cancelled: "Cancelada",
  rejected: "Recusada",
  blocked: "Bloqueado",
};

const requestTypeLabel: Record<string, string> = {
  reschedule: "Alteração de data/hora",
  cancel: "Cancelamento",
  other: "Outro pedido",
};

const ClientPortal = () => {
  const navigate = useNavigate();
  const [session, setSession] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // auth form
  const [mode, setMode] = useState<"login" | "reset">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authBusy, setAuthBusy] = useState(false);

  // data
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [record, setRecord] = useState<any>(null);
  const [requests, setRequests] = useState<ChangeRequest[]>([]);

  // new request
  const [reqBooking, setReqBooking] = useState<string>("");
  const [reqType, setReqType] = useState("reschedule");
  const [reqMessage, setReqMessage] = useState("");
  const [reqBusy, setReqBusy] = useState(false);

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!session) return;
    (async () => {
      try {
        await supabase.rpc("link_client_account");
      } catch (e) {
        console.error("link error", e);
      }

      const { data: roleData } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", session.user.id)
        .eq("role", "admin")
        .maybeSingle();

      const [b, r, cr] = await Promise.all([
        supabase
          .from("bookings")
          .select("id, booking_date, booking_time, status, location_type, address, total_price, notes")
          .eq(roleData ? "client_id" : "id", "dummy-filter-if-admin") // This is a trick to return empty if admin and no client_id match
          .order("booking_date", { ascending: false }),
        supabase.from("client_records").select("*").limit(1),
        supabase.from("booking_change_requests").select("*").order("created_at", { ascending: false }),
      ]);

      if (roleData) {
        // Admins should only see bookings where they are explicitly the client
        const { data: adminBookings } = await supabase
          .from("bookings")
          .select("id, booking_date, booking_time, status, location_type, address, total_price, notes")
          .eq("client_id", session.user.id)
          .order("booking_date", { ascending: false });
        
        const { data: adminRecord } = await supabase
          .from("client_records")
          .select("*")
          .eq("user_id", session.user.id)
          .limit(1);

        setBookings(adminBookings || []);
        setRecord(adminRecord?.[0] || null);
      } else {
        setBookings((b.data as Booking[]) || []);
        setRecord(r.data?.[0] || null);
      }
      setRequests((cr.data as ChangeRequest[]) || []);
    })();
  }, [session]);

  // Real-time booking status for the signed-in client.
  useEffect(() => {
    const uid = session?.user.id;
    if (!uid) return;
    const ch = supabase.channel(`my-bookings-${uid}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "bookings", filter: `client_id=eq.${uid}` }, (p) => {
        const row = p.new as Partial<Booking> & { id?: string };
        if (!row?.id) return;
        setBookings((prev) => {
          const exists = prev.some((x) => x.id === row.id);
          return exists ? prev.map((x) => (x.id === row.id ? { ...x, ...row } as Booking : x)) : [row as Booking, ...prev];
        });
        if (p.eventType === "UPDATE") toast.info("A sua reserva foi atualizada");
      })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [session?.user.id]);

  const handleLogin = async () => {
    setAuthBusy(true);
    const { error } = await supabase.auth.signInWithPassword({ email: email.trim().toLowerCase(), password });
    setAuthBusy(false);
    if (error) {
      toast.error(error.message === "Invalid login credentials" ? "Email ou palavra-passe incorretos." : error.message);
      return;
    }
    toast.success("Bem-vinda de volta!");
  };

  const handleReset = async () => {
    if (!email.trim()) return toast.error("Indique o seu email.");
    setAuthBusy(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email.trim().toLowerCase(), {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setAuthBusy(false);
    if (error) toast.error(error.message);
    else {
      toast.success("Enviámos um email para recuperar a palavra-passe.");
      setMode("login");
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

  const submitRequest = async () => {
    if (reqMessage.trim().length < 5) return toast.error("Descreva o seu pedido.");
    setReqBusy(true);
    const { error } = await supabase.from("booking_change_requests").insert({
      booking_id: reqBooking || null,
      user_id: session.user.id,
      client_email: session.user.email,
      request_type: reqType,
      message: reqMessage.trim().slice(0, 1500),
    });
    setReqBusy(false);
    if (error) {
      toast.error("Não foi possível enviar o pedido.");
      return;
    }
    toast.success("Pedido enviado! A nossa equipa entrará em contacto.");
    setReqMessage("");
    setReqBooking("");
    const { data } = await supabase
      .from("booking_change_requests")
      .select("*")
      .order("created_at", { ascending: false });
    setRequests((data as ChangeRequest[]) || []);
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">A carregar...</div>;
  }

  if (!session) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="max-w-md w-full rounded-2xl border border-border bg-card p-8 space-y-5">
          <div className="text-center space-y-2">
            <ShieldCheck size={40} className="mx-auto text-primary" />
            <h1 className="font-display text-2xl font-bold">Área do Cliente</h1>
            <p className="text-sm text-muted-foreground">
              Entre com o email e a palavra-passe que definiu ao preencher a sua ficha.
            </p>
          </div>

          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" />
            </div>
            {mode === "login" && (
              <div className="space-y-2">
                <Label>Palavra-passe</Label>
                <Input
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  type="password"
                  onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                />
              </div>
            )}
          </div>

          {mode === "login" ? (
            <>
              <Button className="w-full rounded-full" disabled={authBusy} onClick={handleLogin}>
                {authBusy ? "A entrar..." : "Entrar"}
              </Button>
              <button
                className="w-full text-xs text-muted-foreground underline"
                onClick={() => setMode("reset")}
              >
                Esqueci-me da palavra-passe
              </button>
            </>
          ) : (
            <>
              <Button className="w-full rounded-full" disabled={authBusy} onClick={handleReset}>
                {authBusy ? "A enviar..." : "Recuperar palavra-passe"}
              </Button>
              <button className="w-full text-xs text-muted-foreground underline" onClick={() => setMode("login")}>
                Voltar ao início de sessão
              </button>
            </>
          )}

          <div className="pt-2 border-t border-border text-center space-y-2">
            <p className="text-xs text-muted-foreground">Ainda não tem conta?</p>
            <Button asChild variant="outline" className="rounded-full w-full">
              <Link to="/ficha">Preencher a minha ficha</Link>
            </Button>
            <Button asChild variant="ghost" className="rounded-full w-full">
              <Link to="/">Voltar ao início</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft size={16} /> Início
          </Link>
          <div className="text-right">
            <p className="font-display font-bold">Área do Cliente</p>
            <p className="text-xs text-muted-foreground">{session.user.email}</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Ficha */}
        <section className="rounded-2xl border border-border bg-card p-6 space-y-3">
          <h2 className="font-display text-lg font-bold flex items-center gap-2">
            <ClipboardCheck size={18} className="text-primary" /> Ficha de Avaliação e Consentimento
          </h2>
          {record ? (
            <div className="text-sm text-muted-foreground space-y-1">
              <p>
                Ficha registada em{" "}
                <strong>{record.signed_at ? new Date(record.signed_at).toLocaleDateString("pt-PT") : "-"}</strong>.
              </p>
              <p className="text-xs">
                Para atualizar informações de saúde ou preferências, envie um pedido abaixo e a equipa reabre a sua
                ficha.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Ainda não tem ficha preenchida.</p>
              <Button asChild className="rounded-full">
                <Link to="/ficha">Preencher agora</Link>
              </Button>
            </div>
          )}
        </section>

        {/* Reservas */}
        <section className="rounded-2xl border border-border bg-card p-6 space-y-4">
          <h2 className="font-display text-lg font-bold flex items-center gap-2">
            <CalendarDays size={18} className="text-primary" /> As minhas reservas
          </h2>
          {bookings.length === 0 ? (
            <p className="text-sm text-muted-foreground">Ainda não há reservas associadas a este email.</p>
          ) : (
            <div className="space-y-3">
              {bookings.map((b) => (
                <div key={b.id} className="rounded-xl border border-border p-4 space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <p className="font-semibold text-sm">
                      {new Date(b.booking_date + "T00:00:00").toLocaleDateString("pt-PT", {
                        weekday: "long",
                        day: "2-digit",
                        month: "long",
                      })}
                    </p>
                    <Badge variant="secondary">{statusLabel[b.status] || b.status}</Badge>
                  </div>
                  <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Clock size={12} /> {String(b.booking_time).slice(0, 5)}
                    </span>
                    <span className="flex items-center gap-1">
                      <MapPin size={12} />
                      {b.location_type === "home" ? b.address || "Ao domicílio" : "Espaço Mente Sã"}
                    </span>
                    <span>{Number(b.total_price).toFixed(2)} €</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Pedidos */}
        <section className="rounded-2xl border border-border bg-card p-6 space-y-4">
          <h2 className="font-display text-lg font-bold flex items-center gap-2">
            <MessageSquare size={18} className="text-primary" /> Pedir alteração
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Reserva (opcional)</Label>
              <select
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                value={reqBooking}
                onChange={(e) => setReqBooking(e.target.value)}
              >
                <option value="">Sem reserva específica</option>
                {bookings.map((b) => (
                  <option key={b.id} value={b.id}>
                    {new Date(b.booking_date + "T00:00:00").toLocaleDateString("pt-PT")} ·{" "}
                    {String(b.booking_time).slice(0, 5)}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label>Tipo de pedido</Label>
              <select
                className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                value={reqType}
                onChange={(e) => setReqType(e.target.value)}
              >
                <option value="reschedule">Alterar data/hora</option>
                <option value="cancel">Cancelar reserva</option>
                <option value="other">Outro (ficha, dados, dúvidas)</option>
              </select>
            </div>
          </div>
          <div className="space-y-2">
            <Label>Mensagem</Label>
            <Textarea
              rows={4}
              value={reqMessage}
              maxLength={1500}
              onChange={(e) => setReqMessage(e.target.value)}
              placeholder="Explique o que precisa de alterar..."
            />
          </div>
          <Button className="rounded-full" disabled={reqBusy} onClick={submitRequest}>
            {reqBusy ? "A enviar..." : "Enviar pedido"}
          </Button>

          {requests.length > 0 && (
            <div className="pt-4 border-t border-border space-y-3">
              <p className="text-sm font-semibold">Histórico de pedidos</p>
              {requests.map((r) => (
                <div key={r.id} className="rounded-xl border border-border p-3 space-y-1">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-semibold">{requestTypeLabel[r.request_type] || r.request_type}</p>
                    <Badge variant={r.status === "pending" ? "secondary" : "outline"}>
                      {r.status === "pending" ? "Em análise" : r.status === "done" ? "Resolvido" : r.status}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground">{r.message}</p>
                  {r.admin_response && (
                    <p className="text-xs text-primary">Resposta: {r.admin_response}</p>
                  )}
                  <p className="text-[10px] text-muted-foreground">
                    {new Date(r.created_at).toLocaleString("pt-PT")}
                  </p>
                </div>
              ))}
            </div>
          )}
        </section>

        <div className="flex flex-wrap items-center gap-3">
        <PushToggle className="rounded-full" />
        <Button variant="outline" className="rounded-full gap-2" onClick={handleLogout}>
          <LogOut size={14} /> Terminar sessão
        </Button>
        </div>
      </main>
    </div>
  );
};

export default ClientPortal;
